import { Box, CircularProgress, Collapse, Grid, Paper } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/reducers';
import { useLoginUserProviders } from 'context/login-context';
import useTranslation from 'next-translate/useTranslation';
import { LargeText, MediumText, NormalText, SmallText } from 'components/atoms/Typography';
import { useAuth } from 'context/authentication';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { LoginContinueButton, SuccessButton } from 'components/atoms/ButtonsOld';
import GoogleIcon from '@mui/icons-material/Google';
import PhoneIcon from '@mui/icons-material/Phone';
import FacebookIcon from '@mui/icons-material/Facebook';
import AppleIcon from '@mui/icons-material/Apple';
import firebase, { db } from 'config/firebase';
import { useLoginFlow } from 'hooks/useLoginFlow';
import { useSignInProcess } from 'hooks/useSignInProcess';
import { resetUsersLoginInfo, updateUsersLoginInfo } from 'redux/actions/internal/general/users';
import { setValidationInfo } from 'redux/actions/internal/general/validation';
import { BasicInput, PasswordInput } from 'components/atoms/Inputs';
import ValidationMessage from 'components/atoms/ValidationMessage';
import { useValidator } from 'services/validation/validator';
import { ValidationType } from 'types/validation';
import DividerWithText from 'components/molecules/DividerWithText';
import { lowerCase } from 'lodash';

interface IRecognizedPaperProps {
    onLogin?: (user: any, register?: boolean) => void;
}

const RecognizedPaper = ({ onLogin }: IRecognizedPaperProps) => {
    const userProviders = useLoginUserProviders();
    const {
        getByEmail,
        signInWithGoogle,
        signInWithFacebook,
        loginWithCredential,
        persistCredential,
        postUserToken,
        verifyPhoneNumber,
        signInWithEmailAndPassword,
    } = useAuth();
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const authentication = useLoginFlow();
    const dispatch = useDispatch();
    const signInProcess = useSignInProcess();
    const [loading, setLoading] = useState<boolean>(false);
    const [fetchingByEmail, setFetchingByEmail] = useState<boolean>(true);
    const validator = useValidator();
    const passwordRef = useRef<HTMLInputElement>(null);
    const [loginLoading, setLoginLoading] = useState<boolean>(false);

    useEffect(() => {
        setFetchingByEmail(true);
        getByEmail(usersLoginInfo.email).then(res => {
            setFetchingByEmail(false);
            if (res.success) {
                setPhoneNumber(res.data.phoneNumber);
            }
        });
    }, []);

    const usersLoginInfo = useSelector((state: RootState) => state.usersLoginInfo.data);

    const { t } = useTranslation('common');

    const continueWithGoogle = () => {
        signInWithGoogle(usersLoginInfo.email).then(res => {
            db.doc(`/customers/${res.user.uid}`)
                .get()
                .then(data => {
                    if (data.exists) {
                        loginWithCredential(res.credential).then(userCredential => {
                            postUserToken(userCredential.user);
                            authentication.closeLogin();
                            onLogin?.(userCredential.user);
                        });
                    } else {
                        dispatch(
                            updateUsersLoginInfo({
                                email: res.user.email,
                                name: (res.additionalUserInfo.profile as any).given_name,
                                lastName: (res.additionalUserInfo.profile as any).family_name,
                            })
                        );
                        persistCredential(res.credential);
                        signInProcess.goThrough(['SOCIAL_GOOGLE'], 'REGISTER_INFO');
                        authentication.showRegister();
                    }
                });
        });
    };

    const continueWithFacebook = () => {
        signInWithFacebook()
            .then(res => {
                db.doc(`/customers/${res.user.uid}`)
                    .get()
                    .then(data => {
                        if (data.exists) {
                            loginWithCredential(res.credential).then(userCredential => {
                                postUserToken(userCredential.user);
                                authentication.closeLogin();
                                onLogin?.(userCredential.user);
                            });
                        } else {
                            dispatch(
                                updateUsersLoginInfo({
                                    email: res.additionalUserInfo.profile?.['email'],
                                    name: (res.additionalUserInfo.profile as any).first_name,
                                    lastName: (res.additionalUserInfo.profile as any).last_name,
                                })
                            );
                            persistCredential(res.credential);
                            signInProcess.goThrough(['SOCIAL_GOOGLE'], 'REGISTER_INFO');
                            authentication.showRegister();
                        }
                    });
            })
            .catch(err => console.log('error while sign in with Facebook', err));
    };

    const continueWithPhone = () => {
        setLoading(true);
        verifyPhoneNumber()
            .signInWithPhoneNumber(phoneNumber.replace('+', ''))
            .then(() => {
                setLoading(false);
                signInProcess.nextStep('CONFIRM_CODE');
            })
            .catch((error: Error) => {
                setLoading(false);
                dispatch(setValidationInfo({ hasError: true, message: error.message }, 'PHONE_VERIFICATION'));
            });
    };

    // const handleEmailChange = (email: string) => {
    //     validator.validate('EMAIL_LOGIN', { email: usersLoginInfo.email });
    //     dispatch(updateUsersLoginInfo({ email }));
    // };

    const handlePasswordChange = (password: string) => {
        validator.resetValidation('LOGIN_BACKEND_ERROR', 'EMAIL_PASSWORD_LOGIN');
        dispatch(updateUsersLoginInfo({ password }));
    };

    const handleContinue = async (e: FormEvent) => {
        e.preventDefault();
        setLoginLoading(true);

        const type: ValidationType = 'EMAIL_PASSWORD_LOGIN';
        let validationObject: { password?: string; email: string } = {
            email: usersLoginInfo.email,
            password: usersLoginInfo.password,
        };
        const valid = await validator.validate(type, validationObject);

        if (valid) {
            signInWithEmailAndPassword(usersLoginInfo.email, usersLoginInfo.password)
                .then(data => {
                    postUserToken(data.user);
                    dispatch(resetUsersLoginInfo());
                    setLoginLoading(false);
                    onLogin?.(data.user);
                    authentication.closeLogin();
                })
                .catch((error: firebase.auth.Error) => {
                    setLoginLoading(false);
                    console.log(error.code);
                    if (error.code === 'auth/user-not-found') {
                        signInProcess.nextStep('REGISTER_INFO');
                    } else if (error.code === 'auth/wrong-password') {
                        dispatch(
                            setValidationInfo(
                                {
                                    hasError: true,
                                    message: 'Password is invalid',
                                    errorType: 'password',
                                },
                                'LOGIN_BACKEND_ERROR'
                            )
                        );
                    } else {
                        dispatch(
                            setValidationInfo(
                                {
                                    hasError: true,
                                    message: error.message,
                                    errorType: 'other',
                                },
                                'LOGIN_BACKEND_ERROR'
                            )
                        );
                    }
                });
        } else {
            // setCheckForEmail(true);
        }
    };

    return (
        <Paper
            sx={{
                backgroundColor: theme => theme.palette.background.paper,
                boxShadow: theme => theme.shadows[5],
                width: '500px',
                minHeight: '50vh',
                display: 'flex',
                flexDirection: 'column',
                maxWidth: { xs: '90vw', md: 'unset' },
            }}
            style={{ justifyContent: 'center' }}
        >
            <Box
                sx={{
                    py: 4,
                    px: { xs: 2, lg: 10 },
                }}
                position="relative"
            >
                <Box textAlign="center" mt={-4}>
                    <MediumText>{t('recognizedAccountWithEmail')}</MediumText>
                    <Box>
                        <MediumText color="#888888">{usersLoginInfo.email}</MediumText>
                    </Box>
                </Box>
                {!fetchingByEmail && (
                    <Box mt={5}>
                        {userProviders.includes('password') && (
                            <Box>
                                <Grid direction="column" alignItems="center">
                                    <form onSubmit={handleContinue} style={{ width: '100%' }} noValidate>
                                        <Grid item style={{ width: '100%', position: 'relative', paddingBottom: '15px' }}>
                                            <Box mt={1}>
                                                <PasswordInput
                                                    ref={passwordRef}
                                                    onChange={handlePasswordChange}
                                                    error={
                                                        validator.hasError('LOGIN_BACKEND_ERROR', 'password') ||
                                                        validator.hasError('EMAIL_PASSWORD_LOGIN', 'password')
                                                    }
                                                    placeholder={t('password')}
                                                    autoFocus
                                                />
                                                {validator.hasError('EMAIL_PASSWORD_LOGIN', 'password') && (
                                                    <ValidationMessage type="EMAIL_PASSWORD_LOGIN" />
                                                )}
                                                {validator.hasError('LOGIN_BACKEND_ERROR', 'password') && (
                                                    <ValidationMessage type="LOGIN_BACKEND_ERROR" />
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid item style={{ width: '100%' }}>
                                            <Box mb={2}>
                                                <SuccessButton loading={loginLoading}>{t('signIn')}</SuccessButton>
                                            </Box>
                                        </Grid>
                                        <Box mt={1}>
                                            <Grid container justifyContent="flex-start">
                                                {/* <ValidationMessage type="EMAIL_LOGIN" /> */}
                                                {/* <ValidationMessage type="EMAIL_PASSWORD_LOGIN" /> */}
                                                {!validator.hasError('LOGIN_BACKEND_ERROR', 'password') && (
                                                    <ValidationMessage type="LOGIN_BACKEND_ERROR" />
                                                )}
                                            </Grid>
                                        </Box>
                                        <DividerWithText>
                                            {t('or')} {lowerCase(t('continueWith'))}
                                        </DividerWithText>
                                    </form>
                                </Grid>
                            </Box>
                        )}
                        {!userProviders.includes('password') && (
                            <Box textAlign="center">
                                <NormalText>{t('continueWith')}</NormalText>
                            </Box>
                        )}

                        {userProviders.includes('google.com') && (
                            <Box mt={2}>
                                <LoginContinueButton Icon={GoogleIcon} onClick={continueWithGoogle}>
                                    Google
                                </LoginContinueButton>
                            </Box>
                        )}
                        {userProviders.includes('facebook.com') && (
                            <Box mt={2}>
                                <LoginContinueButton Icon={FacebookIcon} onClick={continueWithFacebook}>
                                    {t('continueFacebook')}
                                </LoginContinueButton>
                            </Box>
                        )}
                        {userProviders.includes('apple.com') && (
                            <Box
                                mt={2}
                                sx={{
                                    opacity: 0.5,
                                }}
                            >
                                <LoginContinueButton Icon={AppleIcon} disabled>
                                    Apple (наскоро)
                                </LoginContinueButton>
                            </Box>
                        )}
                        {phoneNumber && (
                            <Box mt={2}>
                                <LoginContinueButton
                                    Icon={PhoneIcon}
                                    onClick={continueWithPhone}
                                    disabled={loading || loginLoading}
                                >
                                    <span>****** {phoneNumber.substring(phoneNumber.length - 3, phoneNumber.length)}</span>
                                </LoginContinueButton>
                                <div id="recaptcha-container"></div>
                            </Box>
                        )}
                        {!phoneNumber && (
                            <Box mt={2} textAlign="center">
                                <SmallText>{t('registrationNotFinished')}</SmallText>
                            </Box>
                        )}
                    </Box>
                )}
                {loading && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Box position="absolute">
                            <Box>
                                <CircularProgress />
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default RecognizedPaper;
